import styled from '@emotion/styled';
import { formatDate } from '~/utils/datetime';

interface IEmailPreviewProps {
  fromEmail: string;
  recipientName: string;
  senderName: string;
  businessName: string;
  content: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  totalAmount: string;
  currency: string;
  invoiceUrl: string;
}

const EmailContainer = styled.div`
  font-family: Arial, sans-serif;
  max-height: 500px;
  overflow-y: auto;
  user-select: none;

  @media (max-width: 900px) {
    max-height: 400px;
  }

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const EmailCard = styled.div`
  background-color: #ffffff;
  border: 1px solid #e6e6e6;
  border-radius: 6px;
  max-width: 100%;
  margin: 0 auto;
`;

const EmailContent = styled.div`
  padding: 0px 20px 15px 20px;

  p {
    color: #555555;
    font-size: 14px;
    line-height: 20px;
    margin: 8px 0;
  }
`;

const InvoiceDetailsCard = styled.div`
  margin: 0 20px 15px 20px;
  border: 1px solid #e6e6e6;
  border-radius: 8px;
  background-color: #f9f9f9;
  padding: 15px;
`;

const InvoiceDetailsTitle = styled.h3`
  font-family: Arial, sans-serif;
  color: #333333;
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 10px 0;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 5px 0;
`;

const DetailLabel = styled.span`
  color: #666666;
  font-size: 12px;
`;

const DetailValue = styled.span`
  color: #333333;
  font-size: 12px;
  font-weight: 600;
`;

const TotalAmount = styled.span`
  color: #5636f5;
  font-size: 12px;
  font-weight: 700;
`;

const ViewButton = styled.div`
  margin: 0 20px 10px 20px;
  padding: 12px;
  background-color: #5636f5;
  border-radius: 5px;
  text-align: center;

  a {
    color: #fff;
    text-decoration: none;
    font-size: 14px;
    font-weight: 500;
    pointer-events: none;
  }
`;

const Footer = styled.div`
  padding: 0px 20px 20px 20px;
  text-align: center;

  p {
    color: #888888;
    font-size: 10px;
    line-height: 14px;
    margin: 0;
  }
`;

const EmailPreview = ({
  fromEmail,
  businessName,
  content,
  invoiceNumber,
  invoiceDate,
  dueDate,
  totalAmount,
  currency,
  invoiceUrl,
}: IEmailPreviewProps) => {
  // Format content to preserve line breaks
  const formattedContent = content
    .split('\n')
    .map((line, index) => <p key={index}>{line || '\u00A0'}</p>);

  return (
    <EmailContainer>
      <EmailCard>
        <EmailContent>
          <p style={{ fontSize: '12px', color: '#888', marginBottom: '12px' }}>From: {fromEmail}</p>
          {formattedContent}
        </EmailContent>

        <InvoiceDetailsCard>
          <InvoiceDetailsTitle>Invoice Details</InvoiceDetailsTitle>
          <DetailRow>
            <DetailLabel>Invoice Number:</DetailLabel>
            <DetailValue>{invoiceNumber}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Invoice Date:</DetailLabel>
            <DetailValue>{formatDate(invoiceDate)}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Due Date:</DetailLabel>
            <DetailValue>{formatDate(dueDate)}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Total Amount:</DetailLabel>
            <TotalAmount>
              {currency} {totalAmount}
            </TotalAmount>
          </DetailRow>
        </InvoiceDetailsCard>

        <ViewButton>
          <a href={invoiceUrl} target="_blank" rel="noopener noreferrer">
            View Invoice Online
          </a>
        </ViewButton>

        <Footer>
          <p>Sent by {businessName} using createmyinvoice.com</p>
        </Footer>
      </EmailCard>
    </EmailContainer>
  );
};

export default EmailPreview;
